import { IVeriTableHeader, IVeriTableRow, IVeriTableRowCell } from "@directives/_veri/veriTable/veriTable.component";
import { VeriTableService } from "@directives/_veri/veriTable/veriTable.service";
import { ResourceType } from "@enums/ResourceType";
import { SpecialUserPermissions } from "@enums/SpecialUserPermissions";
import { IRunBlockRootScope } from "@interfaces/IRunBlockRootScope";
import { IUser } from "@interfaces/IUser";
import { EnumService } from "@services/enum.service";
import { AccountFeatureService } from "@services/accountFeature.service";
import { TranslationService } from "@services/translation.service";
import * as angular from "angular";
import "angular-material";
import copy from "fast-copy/dist/fast-copy.esm.js";
import { AlertCategory } from "src/app/main/modules/alerts/_enums/alerts.enums";
import { IAlertFilterCriteria, IAlertRule, INotificationQuota } from "../../_models/alerts.interfaces";
import { AlertsService, IAlertCategoryTrigger } from "../../_services/alerts.service";
import { SortType } from "@enums/SortType";
import "./alertsManager.component.scss";
import { AlertRuleStatus } from "src/app/main/modules/alerts/_enums/AlertRuleStatus.enums";
import { DateService } from "@app/services/date.service";

interface IAlertUsageDashboardBoxInfo {
    emailUsageTitle: string;
    emailUsageSubtitle: string;

    smsUsageTitle: string;
    smsUsageSubtitle: string;
}

class AlertsManagerComponentController implements angular.IComponentController {

    // raw data
    alertRules: IAlertRule[] = [];
    notificationQuotas: INotificationQuota;

    // loaders
    loadingAlertRules = false;

    // --
    alertCategory = AlertCategory;
    alertCategoryTriggers: IAlertCategoryTrigger[] = [];
    resourceType = ResourceType;

    selectedAlert: IAlertRule;
    filteredAlertRules: IAlertRule[] = [];
    displayRules: IAlertRule[] = [];

    alertCategories: any[] = [];

    // access
    currentUser: IUser;
    isSystemUser = false;
    canManageAlerts: boolean = false;

    alertsDate: Date;

    // dataview
    tableHeaders: IVeriTableHeader[] = [];
    tableRows: IVeriTableRow<IAlertRule>[] = [];

    usageDashboardBoxInfo: IAlertUsageDashboardBoxInfo;

    static $inject = ["$q", "$rootScope", "$filter", "accountFeatureService", "alertsService", "veriTableService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $rootScope: IRunBlockRootScope,
        private readonly $filter: angular.IFilterService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly alertsService: AlertsService,
        private readonly veriTableService: VeriTableService,
        private readonly translationService: TranslationService
    ) {

        this.currentUser = angular.copy($rootScope.currentUser);
        this.isSystemUser = this.currentUser.CurrentAccountUserMapping.Type === 0;
        this.canManageAlerts = this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ManageAlerts);
    }

    $onInit() {
        this.alertCategoryTriggers = this.alertsService.createAlertTriggerMappings();

        this.configureDataViewTable();
        this.alertCategories = EnumService.enumToArray("AlertCategory", AlertCategory);
        this.alertCategories.sort((cat1, cat2) => (cat1.value > cat2.value) ? 1 : -1);

        this.alertsDate = new Date();

        this.refreshData();
    }

    onAlertEditorCancelled(): void {
        this.clearSelectedAlertRule();
        this.refreshData();
    }

    onAlertEditorDeleted(): void {
        this.clearSelectedAlertRule();
        this.refreshData();
    }

    onAlertEditorSaved(): void {
        this.clearSelectedAlertRule();
        this.refreshData();
    }

    selectAlert($event: MouseEvent, alert: IAlertRule): void {
        this.selectedAlert = copy(alert);

        this.showAlertEditorDialog($event);
    }

    createNewAlertRule($event: MouseEvent): void {
        this.showAlertEditorDialog($event, true);
    }

    searchAlertRules(searchString: string) {
        this.filteredAlertRules = [];

        this.alertRules?.forEach(alertRule => {
            if (this.isSystemUser || this.canAccessAlert(alertRule)) {
                this.filteredAlertRules.push(alertRule);
            }
        });

        if (!searchString) {
            this.displayRules = this.filteredAlertRules;
            return;
        }

        searchString = searchString.toLowerCase();

        this.displayRules = [];
        this.filteredAlertRules?.forEach(rule => {
            if (rule.Name.toLowerCase().indexOf(searchString) >= 0) {
                this.displayRules.push(rule);
            }
        });
    }

    // -----

    private refreshData(): void {
        this.loadingAlertRules = true;

        this.getData().then(() => {
            this.createDataViewTableRows();
            this.renderDashboardBoxes();

            this.loadingAlertRules = false;
        });
    }

    private getData(): angular.IPromise<any> {
        const alertRulesPromise = this.alertsService.getActiveAlertRules();
        const quotasPromise = this.alertsService.getNotificationQuotas(new Date());

        return this.$q.all([alertRulesPromise, quotasPromise]).then(([alertRules, notificationQuotas]) => {
            this.alertRules = alertRules;
            this.notificationQuotas = notificationQuotas;
            this.addNotificationQuotaPercentages(this.notificationQuotas);
            this.searchAlertRules(null);
        });
    }

    private clearSelectedAlertRule(): void {
        this.selectedAlert = null;
    }

    private showAlertEditorDialog($event: MouseEvent, forceNewRuleMode: boolean = false) {
        const newRuleMode: boolean = forceNewRuleMode || !this.selectedAlert?.Id;
        const alertRule: IAlertRule = (newRuleMode) ? null : this.selectedAlert;

        this.alertsService.showAlertEditor(alertRule, $event, {
            onAlertEditorCancelled: () => { this.onAlertEditorCancelled(); },
            onAlertEditorDeleted: () => { this.onAlertEditorDeleted(); },
            onAlertEditorSaved: () => { this.onAlertEditorSaved(); }
        });
    }

    private canAccessAlert(alertRule: IAlertRule): boolean {
        if (!this.canManageAlerts) {
            return false;
        }

        let canAccessAlert = true;
        alertRule.Filters.forEach(filter => {
            canAccessAlert = canAccessAlert && this.hasFilterAccess(filter) && this.hasGroupAccess(filter.GroupId);
        });

        return canAccessAlert;
    }

    private hasFilterAccess(filter: IAlertFilterCriteria) {
        return filter.All || this.hasResourceTypeAccess(filter.ResourceType);
    }

    private hasGroupAccess(groupId: number): boolean {
        return !groupId || this.currentUser.GroupIds && this.currentUser.GroupIds.firstOrDefault<number>(g => g === groupId) > 0;
    }

    private hasResourceTypeAccess(resourceType: ResourceType): boolean {
        switch (resourceType) {
            case ResourceType.Driver:
                return this.currentUser.CurrentAccountUserMapping.AllowAllDrivers;

            case ResourceType.Geofence:
                return this.currentUser.CurrentAccountUserMapping.AllowAllGeofences;

            case ResourceType.Vehicle:
                return this.currentUser.CurrentAccountUserMapping.AllowAllVehicles;
        }

        return false;
    }

    private addNotificationQuotaPercentages(notificationQuotas: INotificationQuota): void {
        const emailPercentageUsed: number = (notificationQuotas.TotalUsed.Email / notificationQuotas.TotalAllowed.Email) * 100;
        const smsPercentageUsed: number = (notificationQuotas.TotalUsed.Sms / notificationQuotas.TotalAllowed.Sms) * 100;

        notificationQuotas.emailPercentageUsed = +emailPercentageUsed.toFixed(2);
        notificationQuotas.smsPercentageUsed = +smsPercentageUsed.toFixed(2);
    }

    private configureDataViewTable(): void {
        this.createDataViewTableHeaders();
    }

    private createDataViewTableHeaders() {
        this.tableHeaders = [
            { title: this.translationService.t("AlertsManager.name"), sortable: true },
            { title: this.translationService.t("AlertsManager.category"), sortable: true, width: 220 },
            { title: this.translationService.t("AlertsManager.subCategory"), sortable: true, width: 220 },
            { title: this.translationService.t("AlertsManager.resendInterval"), sortable: true, width: 160 },
            { title: this.translationService.t("AlertsManager.delay"), sortable: true, width: 160 },
            { title: this.translationService.t("Common.Status"), sortable: true, sortType: SortType.String },
            this.veriTableService.createDateTimeHeader(this.translationService.t("AlertsManager.lastSentTime"))
        ];
    }

    private createDataViewTableRows() {
        this.tableRows = this.veriTableService.createTableRowsWithCells(this.displayRules, rule => this.createTableRowCells(rule));
    }

    private createTableRowCells(alertRule: IAlertRule): IVeriTableRowCell[] {

        const category = this.translationService.tEnum(AlertCategory, 'AlertCategory', alertRule.Category);
        const { subCategoryEnum, subCategoryString } = this.alertsService.getAlertRuleSubcategoryEnum(alertRule.Category);
        const subCategory = this.translationService.tEnum(subCategoryEnum, subCategoryString, alertRule.SubCategory);
        const resendPluralWord = (alertRule.ResendMinuteInterval < 60) ? 'minute' : 'hour';
        const resendPluralNumber = (alertRule.ResendMinuteInterval < 60) ? alertRule.ResendMinuteInterval : alertRule.ResendMinuteInterval / 60;

        const resendLabel: string = (alertRule.ResendMinuteInterval) ? `${resendPluralNumber} ${this.pluralize(resendPluralWord, resendPluralNumber)}` : `-`;
        const delayLabel: string = (alertRule.FirstNotificationDelay) ? `${alertRule.FirstNotificationDelay} ${this.pluralize('minute', alertRule.FirstNotificationDelay)}` : `-`;

        return [
            { contents: `${alertRule.Name}` },
            { contents: category },
            { contents: subCategory },
            { contents: `${resendLabel}` },
            { contents: `${delayLabel}` },
            { contents: alertRule.Status === AlertRuleStatus.Active ? this.translationService.t("AlertsManager.Active") : this.translationService.t("AlertsManager.Paused") },
            this.veriTableService.createDateTimeCell(alertRule.LastSentTime)
        ];
    }

    private pluralize(label: string, length: number): string {
        return (length === 1) ? `${label}` : `${label}s`;
    }

    private renderDashboardBoxes(): void {

        const numberFilter: angular.IFilterNumber = this.$filter("number");

        const emailPercentageUsed = this.notificationQuotas.emailPercentageUsed;
        const totalEmailsUsed = numberFilter(this.notificationQuotas.TotalUsed.Email);
        const totalEmailsAllowed = numberFilter(this.notificationQuotas.TotalAllowed.Email);

        const smsPercentageUsed = this.notificationQuotas.smsPercentageUsed;
        const totalSmsUsed = numberFilter(this.notificationQuotas.TotalUsed.Sms);
        const totalSmsAllowed = numberFilter(this.notificationQuotas.TotalAllowed.Sms);

        this.usageDashboardBoxInfo = {
            emailUsageTitle: this.translationService.t("AlertsManager.dashboardBoxTitleEmailUsage", emailPercentageUsed),
            emailUsageSubtitle: this.translationService.t("AlertsManager.dashboardBoxSubtitleEmailUsage", [totalEmailsUsed, totalEmailsAllowed]),
            smsUsageTitle: this.translationService.t("AlertsManager.dashboardBoxTitleSmsUsage", smsPercentageUsed),
            smsUsageSubtitle: this.translationService.t("AlertsManager.dashboardBoxSubtitleSmsUsage", [totalSmsUsed, totalSmsAllowed])
        }
    }
}

export class AlertsManagerComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
        };

        this.controller = AlertsManagerComponentController;
        this.template = require("./alertsManager.component.html");
    }
}
